import React, { useState, useEffect } from 'react';
import { Container, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as api from '~/Services/Service';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import Loading from '~/Components/Loading';
import FiltrosImoveisEstoque from './components/FiltrosImoveisEstoque';
import TabelaImoveisEstoque from './components/TabelaImoveisEstoque';

const ImoveisEstoque = () => {
  const [resultado, setResultado] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportandoExcel, setIsExportandoExcel] = useState(false);
  const [isAbrindoReporting, setIsAbrindoReporting] = useState(false);
  const [opcoesGes, setOpcoesGes] = useState([]);
  const [opcoesGruposGe, setOpcoesGruposGe] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [filtrosAtivos, setFiltrosAtivos] = useState(null);
  const [mostrarPontuacao, setMostrarPontuacao] = useState(false);
  // Auto-seleção GE (SetaGE simplificado: se size==1, pré-seleciona)
  const [geAutoSelecionada, setGeAutoSelecionada] = useState(null);

  // Carga inicial: GEs e Grupos-GE (anti-race, LICOES ciclo 9 L3)
  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      try {
        const [ges, grupos] = await Promise.all([
          api.listarGruposEmpresaImoveisEstoque(),
          api.listarGruposGeImoveisEstoque(),
        ]);
        if (!ativo) return;
        const listaGes = Array.isArray(ges) ? ges : [];
        setOpcoesGes(listaGes);
        setOpcoesGruposGe(Array.isArray(grupos) ? grupos : []);
        if (listaGes.length === 1) {
          setGeAutoSelecionada([{
            value: listaGes[0].codigo_grupo_empresa,
            label: listaGes[0].apelido,
          }]);
        }
      } catch (err) {
        if (!ativo) return;
        const msg = err && err.message ? err.message : 'Erro ao carregar GEs.';
        toast.error(msg);
      }
    };
    carregar();
    return () => { ativo = false; };
  }, []);

  const handleBuscar = async (filtros) => {
    setIsLoading(true);
    setResultado(null);
    setPagina(1);
    setFiltrosAtivos(filtros);
    setMostrarPontuacao(!!filtros.pontuacao);
    try {
      const res = await api.consultarImoveisEstoque(filtros);
      setResultado(Array.isArray(res) ? res : []);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao consultar imóveis em estoque.';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLimpar = () => {
    setResultado(null);
    setFiltrosAtivos(null);
    setPagina(1);
    setMostrarPontuacao(false);
  };

  const handleExportarExcel = async () => {
    if (!filtrosAtivos) {
      toast.warn('Consulte o relatório antes de exportar.');
      return;
    }
    setIsExportandoExcel(true);
    try {
      const blob = await api.exportarImoveisEstoqueExcel(filtrosAtivos);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const dataHora = moment().format('YYYY-MM-DD_HHmmss');
      link.setAttribute('download', `ImoveisEstoque_${dataHora}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao exportar Excel.';
      toast.error(msg);
    } finally {
      setIsExportandoExcel(false);
    }
  };

  const handleAbrirReporting = async () => {
    if (!filtrosAtivos) {
      toast.warn('Consulte o relatório antes de abrir o Reporting.');
      return;
    }
    setIsAbrindoReporting(true);
    try {
      const blob = await api.abrirImoveisEstoqueReporting(filtrosAtivos);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao abrir Reporting.';
      toast.error(msg);
    } finally {
      setIsAbrindoReporting(false);
    }
  };

  const valoresIniciaisOverride = geAutoSelecionada
    ? { gesMulti: geAutoSelecionada }
    : null;

  const temResultado = resultado && resultado.length > 0;

  return (
    <Container fluid className="px-3 py-3">
      <HeaderAzul title="Imóveis em Estoque" />

      <FiltrosImoveisEstoque
        onSubmit={handleBuscar}
        onLimpar={handleLimpar}
        isLoading={isLoading}
        opcoesGes={opcoesGes}
        opcoesGruposGe={opcoesGruposGe}
        valoresIniciais={valoresIniciaisOverride}
      />

      {temResultado && !isLoading && (
        <div className="d-flex mb-2">
          <Button
            variant="success"
            size="sm"
            onClick={handleExportarExcel}
            disabled={isExportandoExcel}
            className="mr-2"
          >
            {isExportandoExcel ? 'Exportando...' : 'Exportar Excel'}
          </Button>
          <Button
            variant="info"
            size="sm"
            onClick={handleAbrirReporting}
            disabled={isAbrindoReporting}
          >
            {isAbrindoReporting ? 'Abrindo...' : 'Reporting (PDF)'}
          </Button>
        </div>
      )}

      {isLoading && <Loading label="Consultando imóveis em estoque..." />}

      {!isLoading && resultado !== null && resultado.length === 0 && (
        <div className="alert alert-info mt-3">
          Nenhum imóvel encontrado com os filtros selecionados.
        </div>
      )}

      {!isLoading && temResultado && (
        <TabelaImoveisEstoque
          resultado={resultado}
          pagina={pagina}
          onPaginaChange={setPagina}
          mostrarPontuacao={mostrarPontuacao}
        />
      )}
    </Container>
  );
};

export default ImoveisEstoque;
